import React from 'react';
import { usePayerWizardContext } from '../PayerWizardContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight } from 'lucide-react';
import { PAYER_TYPES, US_STATES } from '@/lib/payer-wizard/types';
import type { PayerType } from '@/lib/rules/rulesetTypes';

export function PayerStep1Setup() {
  const { progress, updatePayerSetup, validateStep1, completeStep } = usePayerWizardContext();
  
  if (!progress) return null;
  
  const validation = validateStep1();
  
  const handleContinue = () => {
    if (validation.isValid) {
      completeStep(1);
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Step 1: Payer Setup
        </CardTitle>
        <CardDescription>
          Configure basic payer information. Submission lanes are configured per-plan in Step 4.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Payer Name */}
        <div className="space-y-2">
          <Label htmlFor="payer_name">Payer Name *</Label>
          <Input
            id="payer_name"
            value={progress.payer_name}
            onChange={(e) => updatePayerSetup({ payer_name: e.target.value })}
            placeholder="e.g., Anthem Blue Cross Blue Shield"
          />
        </div>
        
        {/* Payer Type */}
        <div className="space-y-2">
          <Label htmlFor="payer_type">Payer Type *</Label>
          <Select
            value={progress.payer_type}
            onValueChange={(value: PayerType) => updatePayerSetup({ payer_type: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select payer type" />
            </SelectTrigger>
            <SelectContent>
              {PAYER_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Jurisdiction */}
        <div className="space-y-2">
          <Label htmlFor="jurisdiction">Jurisdiction / State *</Label>
          <Select
            value={progress.jurisdiction}
            onValueChange={(value) => updatePayerSetup({ jurisdiction: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((state) => (
                <SelectItem key={state.value} value={state.value}>
                  {state.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Optional Contact Info */}
        <div className="space-y-4 pt-2 border-t">
          <p className="text-sm text-muted-foreground">
            Optional: Add payer contact details (can also be set per-plan in Step 4)
          </p>
          
          <div className="space-y-2">
            <Label htmlFor="portal_url">Portal URL</Label>
            <Input
              id="portal_url"
              type="url"
              value={progress.portal_url || ''}
              onChange={(e) => updatePayerSetup({ portal_url: e.target.value || null })}
              placeholder="https://provider.payer.com"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={progress.phone || ''}
                onChange={(e) => updatePayerSetup({ phone: e.target.value || null })}
                placeholder="1-800-555-0000"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="fax">Fax</Label>
              <Input
                id="fax"
                type="tel"
                value={progress.fax || ''}
                onChange={(e) => updatePayerSetup({ fax: e.target.value || null })}
                placeholder="1-800-555-0001"
              />
            </div>
          </div>
        </div>
        
        {/* Validation Errors */}
        {!validation.isValid && validation.errors.length > 0 && (
          <div className="bg-destructive/10 text-destructive border border-destructive/30 rounded-lg p-3">
            <ul className="list-disc list-inside text-sm">
              {validation.errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Continue Button */}
        <div className="flex justify-end pt-4">
          <Button onClick={handleContinue} disabled={!validation.isValid}>
            Continue to Plan Setup
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
